/**
 * Student Dashboard (Playful / Duolingo style)
 * Colorful hero, live stats, segmented pill tabs, subject-colored course cards
 * with progress rings, skeleton loaders and friendly empty states.
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/layout/Layout';
import Mascot from '../../components/shared/Mascot';
import CourseCardPlayful from '../../components/courses/CourseCardPlayful';
import CourseCardSkeleton from '../../components/courses/CourseCardSkeleton';
import UpNextCard from '../../components/shared/UpNextCard';
import {
  getCourses,
  getEnrolledCourses,
  enrollInCourse,
  CourseResponse,
  getContinueWatching,
  ContinueWatchingItem,
  getUpcomingQuizzes,
  UpcomingQuizItem,
} from '../../api/courses';
import { startReminderChecker } from '../../utils/reminders';

type TabType = 'browse' | 'enrolled';

const StudentDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState<TabType>('enrolled');
  const [allCourses, setAllCourses] = useState<CourseResponse[]>([]);
  const [enrolledCourses, setEnrolledCourses] = useState<CourseResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [semesterFilter, setSemesterFilter] = useState<string>('all');
  const [continueWatching, setContinueWatching] = useState<ContinueWatchingItem[]>([]);
  const [upcomingQuizzes, setUpcomingQuizzes] = useState<UpcomingQuizItem[]>([]);
  // Ref so the reminder checker always sees the freshest items
  const upcomingRef = useRef<UpcomingQuizItem[]>([]);
  upcomingRef.current = upcomingQuizzes;

  useEffect(() => {
    getContinueWatching().then(setContinueWatching).catch(() => {});
  }, []);

  useEffect(() => {
    getUpcomingQuizzes().then(setUpcomingQuizzes).catch(() => {});
  }, []);

  useEffect(() => startReminderChecker(() => upcomingRef.current), []);

  useEffect(() => {
    loadCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadCourses = async () => {
    try {
      setLoading(true);
      setError('');
      const [all, enrolled] = await Promise.all([getCourses(), getEnrolledCourses()]);
      setAllCourses(all);
      setEnrolledCourses(enrolled);
    } catch (err: any) {
      setError('Failed to load courses');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async (courseId: string) => {
    try {
      await enrollInCourse(courseId);
      await loadCourses();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to enroll in course');
    }
  };

  const isEnrolled = (courseId: string) => enrolledCourses.some((c) => c.course_id === courseId);

  const filterCourses = (courses: CourseResponse[]) =>
    courses.filter((course) => {
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return (
        course.title.toLowerCase().includes(q) ||
        (course.instructor_name || course.instructor).toLowerCase().includes(q) ||
        (course.description || '').toLowerCase().includes(q)
      );
    });

  const filteredEnrolled = useMemo(() => filterCourses(enrolledCourses), [enrolledCourses, searchQuery]);
  const filteredAll = useMemo(() => filterCourses(allCourses), [allCourses, searchQuery]);

  const baseList = activeTab === 'enrolled' ? filteredEnrolled : filteredAll;

  // Semester filtering — terms derived from the loaded courses, newest first
  const SEMESTER_ORDER: Record<string, number> = { Spring: 0, Summer: 1, Fall: 2 };
  const termOf = (c: CourseResponse) => (c.semester && c.year ? `${c.semester} ${c.year}` : null);
  const termSource = activeTab === 'enrolled' ? enrolledCourses : allCourses;
  const terms = Array.from(new Set(termSource.map(termOf).filter((t): t is string => t !== null))).sort((a, b) => {
    const [sa, ya] = a.split(' ');
    const [sb, yb] = b.split(' ');
    return Number(yb) - Number(ya) || (SEMESTER_ORDER[sb] ?? 0) - (SEMESTER_ORDER[sa] ?? 0);
  });
  const hasUntermed = termSource.some((c) => !termOf(c));
  const activeList =
    semesterFilter === 'all'
      ? baseList
      : semesterFilter === 'none'
        ? baseList.filter((c) => !termOf(c))
        : baseList.filter((c) => termOf(c) === semesterFilter);

  const tabs: { id: TabType; label: string; count: number }[] = [
    { id: 'enrolled', label: 'My Courses', count: filteredEnrolled.length },
    { id: 'browse', label: 'Explore', count: filteredAll.length },
  ];

  return (
    <Layout>
      <div className="space-y-8">
        {/* === Header === */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight text-gray-900 dark:text-white sm:text-3xl">
              Welcome back, {user?.full_name || user?.username || 'there'}
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
            </p>
          </div>
        </div>

        {/* === Up next: upcoming quiz deadlines === */}
        {upcomingQuizzes.length > 0 && (
          <div className="space-y-3">
            <h2 className="text-lg font-extrabold tracking-tight text-gray-900 dark:text-white">
              Up next
            </h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {upcomingQuizzes.slice(0, 3).map((item) => (
                <UpNextCard
                  key={item.quiz_id}
                  item={item}
                  onOpen={() => navigate(`/student/courses/${item.course_id}`)}
                />
              ))}
            </div>
          </div>
        )}

        {/* === Continue watching === */}
        {continueWatching.length > 0 && (
          <div className="space-y-3">
            <h2 className="text-lg font-extrabold tracking-tight text-gray-900 dark:text-white">
              Continue watching
            </h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {continueWatching.map((item) => {
                const minutesLeft =
                  item.duration_seconds > 0
                    ? Math.max(1, Math.round((item.duration_seconds - item.position_seconds) / 60))
                    : null;
                return (
                  <button
                    key={item.lecture_id}
                    onClick={() => navigate(`/student/courses/${item.course_id}/lectures/${item.lecture_id}`)}
                    className="group flex flex-col rounded-2xl border border-gray-200 bg-white p-4 text-left shadow-soft transition-all duration-200 hover:-translate-y-0.5 hover:border-primary-300 hover:shadow-medium focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-500/20 dark:border-secondary-700 dark:bg-secondary-800 dark:hover:border-primary-800"
                  >
                    <div className="flex items-center gap-3">
                      <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300">
                        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M10 8.5l5 3.5-5 3.5z" /></svg>
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-bold text-gray-900 dark:text-white">{item.lecture_title}</p>
                        <p className="truncate text-xs text-gray-500 dark:text-gray-400">{item.course_title}</p>
                      </div>
                    </div>
                    <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-gray-100 dark:bg-secondary-700">
                      <div
                        className="h-full rounded-full bg-primary-500 transition-all"
                        style={{ width: `${Math.min(100, Math.max(2, item.percent))}%` }}
                      />
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                        {minutesLeft !== null ? `${minutesLeft} min left` : `${Math.round(item.percent)}% watched`}
                      </span>
                      <span className="text-xs font-bold text-primary-700 transition-colors group-hover:text-primary-800 dark:text-primary-300">
                        Resume
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="inline-flex rounded-full bg-gray-100 p-1 dark:bg-secondary-800">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-bold transition-all duration-300 ${
                  activeTab === tab.id
                    ? 'bg-white text-primary-700 shadow-sm dark:bg-secondary-700 dark:text-white'
                    : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
              >
                {tab.label}
                <span className={`rounded-full px-1.5 py-0.5 text-[11px] font-bold ${
                  activeTab === tab.id
                    ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
                    : 'bg-gray-200 text-gray-500 dark:bg-secondary-700 dark:text-gray-400'
                }`}>{tab.count}</span>
              </button>
            ))}
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            {!loading && terms.length > 0 && (
              <div className="flex items-center gap-2">
                <label htmlFor="semester-filter" className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                  Semester
                </label>
                <select
                  id="semester-filter"
                  value={semesterFilter}
                  onChange={(e) => setSemesterFilter(e.target.value)}
                  className="rounded-full border-2 border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 dark:border-secondary-600 dark:bg-secondary-800 dark:text-gray-200"
                >
                  <option value="all">All semesters</option>
                  {terms.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                  {hasUntermed && <option value="none">No semester set</option>}
                </select>
              </div>
            )}

            <div className="relative sm:w-72">
              <svg className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
              <input
                type="text"
                placeholder="Search courses, instructors..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-full border-2 border-gray-200 bg-white py-2.5 pl-10 pr-4 text-sm text-gray-900 placeholder-gray-400 transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 dark:border-secondary-700 dark:bg-secondary-800 dark:text-white dark:placeholder-secondary-400"
              />
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-3 rounded-2xl border-l-4 border-red-500 bg-red-50 px-4 py-3 text-red-700 dark:bg-red-900/20 dark:text-red-400">
            <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 3a9 9 0 100 18 9 9 0 000-18z" /></svg>
            {error}
          </div>
        )}

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => <CourseCardSkeleton key={i} />)}
          </div>
        ) : activeList.length === 0 && baseList.length > 0 ? (
          <div className="rounded-2xl border border-gray-200 bg-white px-6 py-10 text-center dark:border-secondary-700 dark:bg-secondary-800">
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
              No courses in {semesterFilter === 'none' ? 'this view' : semesterFilter}.
            </p>
            <button
              onClick={() => setSemesterFilter('all')}
              className="mt-3 text-sm font-semibold text-primary-700 hover:text-primary-800 dark:text-primary-300 dark:hover:text-primary-200"
            >
              Show all semesters
            </button>
          </div>
        ) : activeList.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-gray-300 bg-white/60 px-6 py-16 text-center dark:border-secondary-700 dark:bg-secondary-800/40">
            <Mascot
              name={searchQuery ? 'folder' : activeTab === 'enrolled' ? 'book' : 'bulb'}
              chip={searchQuery ? '#93c5fd' : activeTab === 'enrolled' ? '#fbbf24' : '#fb923c'}
              className="h-20 w-20 shadow-medium animate-float-slow"
              blinkDelay={0.3}
            />
            <h3 className="mt-5 text-lg font-extrabold tracking-tight text-gray-900 dark:text-white">
              {searchQuery ? 'No matching courses' : activeTab === 'enrolled' ? 'No courses yet' : 'Nothing to explore right now'}
            </h3>
            <p className="mt-1.5 max-w-sm text-sm text-gray-500 dark:text-gray-400">
              {searchQuery ? 'Try a different search.' : activeTab === 'enrolled' ? 'Enroll in a course to start learning with your AI tutor.' : 'Check back soon. New courses are added regularly.'}
            </p>
            {!searchQuery && activeTab === 'enrolled' && (
              <button onClick={() => setActiveTab('browse')} className="mt-6 rounded-full bg-primary-600 px-6 py-2.5 text-sm font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none">
                Explore courses
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
            {activeList.map((course) => {
              const enrolled = isEnrolled(course.course_id);
              const isEnrollAction = activeTab === 'browse' && !enrolled;
              return (
                <CourseCardPlayful
                  key={course.course_id}
                  course={course}
                  enrolled={enrolled}
                  ctaLabel={isEnrollAction ? 'Enroll' : 'Start'}
                  onClick={() =>
                    isEnrollAction
                      ? handleEnroll(course.course_id)
                      : navigate(`/student/courses/${course.course_id}`)
                  }
                />
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default StudentDashboard;
